import { RoleType } from '../types';

// 投票记录类型
export interface VoteRecord {
  round: number;
  teamSize: number;
  leader: string;
  team: string[];
  votes: Record<string, 'approve' | 'reject'>;
  result: 'approved' | 'rejected' | null;
}

// 任务结果类型
export type MissionResult = 'pending' | 'success' | 'fail';

export interface MissionRecord {
  result: MissionResult;
  failVotes?: number;
}

// Storage Keys
const STORAGE_KEYS = {
  CURRENT_GAME: 'avalon_current_game',
  ROUND_DATA: 'avalon_round_data',
  MISSION_RESULTS: 'avalon_mission_results',
  PLAYER_ROLE_MARKS: 'avalon_player_role_marks',
  GAME_NOTES: 'avalon_game_notes_v2', // V43: 升级为支持多 tab 的笔记
  IS_GAME_LOCKED: 'avalon_is_game_locked',
} as const;

// V43: 笔记数据结构
export interface PlayerNotes {
  public: string;
  [playerId: string]: string; // playerId 格式: "player_0", "player_1" 等
}

// V43: 保存多 tab 笔记
export const savePlayerNotes = (notes: PlayerNotes): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.GAME_NOTES, JSON.stringify(notes));
  } catch (error) {
    console.error('Failed to save player notes:', error);
  }
};

// V43: 加载多 tab 笔记
export const loadPlayerNotes = (): PlayerNotes => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.GAME_NOTES);
    if (!data) return { public: '' };
    const parsed = JSON.parse(data);
    // 确保有 public 字段
    if (!parsed.public) parsed.public = '';
    return parsed;
  } catch (error) {
    console.error('Failed to load player notes:', error);
    return { public: '' };
  }
};

// V43: 保存游戏锁定状态
export const saveGameLockState = (isLocked: boolean): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.IS_GAME_LOCKED, JSON.stringify(isLocked));
  } catch (error) {
    console.error('Failed to save game lock state:', error);
  }
};

// V43: 加载游戏锁定状态
export const loadGameLockState = (): boolean => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.IS_GAME_LOCKED);
    return data ? JSON.parse(data) : false;
  } catch (error) {
    console.error('Failed to load game lock state:', error);
    return false;
  }
};

// 游戏状态数据结构 (V89: playerRoleMarks 支持多选)
export interface GameState {
  playerCount: number;
  players: string[];
  currentRound: number;
  leaderIndex: number;
  votes: VoteRecord[];
  missionResults: MissionRecord[];
  playerRoleMarks: Record<number, RoleType[]>;
  lastUpdated: number;
}

// 轮次数据结构
export interface RoundData {
  round: number;
  leader: string;
  team: string[];
  votes: Record<string, 'approve' | 'reject'>;
  result: 'approved' | 'rejected' | null;
  missionResult?: MissionRecord;
}

/**
 * 保存游戏状态到 localStorage
 */
export const saveGameState = (state: GameState): void => {
  try {
    const data = {
      ...state,
      lastUpdated: Date.now(),
    };
    localStorage.setItem(STORAGE_KEYS.CURRENT_GAME, JSON.stringify(data));
  } catch (error) {
    console.error('Failed to save game state:', error);
  }
};

/**
 * 从 localStorage 加载游戏状态
 */
export const loadGameState = (): GameState | null => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.CURRENT_GAME);
    if (!data) return null;
    
    const parsed = JSON.parse(data) as GameState;
    
    // 验证数据有效性（24小时内）
    const maxAge = 24 * 60 * 60 * 1000; // 24小时
    if (Date.now() - parsed.lastUpdated > maxAge) {
      clearGameState();
      return null;
    }
    
    return parsed;
  } catch (error) {
    console.error('Failed to load game state:', error);
    return null;
  }
};

/**
 * 保存轮次数据到 localStorage
 */
export const saveRoundData = (rounds: RoundData[]): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.ROUND_DATA, JSON.stringify(rounds));
  } catch (error) {
    console.error('Failed to save round data:', error);
  }
};

/**
 * 从 localStorage 加载轮次数据
 */
export const loadRoundData = (): RoundData[] => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.ROUND_DATA);
    return data ? JSON.parse(data) : [];
  } catch (error) {
    console.error('Failed to load round data:', error);
    return [];
  }
};

/**
 * 保存任务结果到 localStorage
 */
export const saveMissionResults = (results: MissionRecord[]): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.MISSION_RESULTS, JSON.stringify(results));
  } catch (error) {
    console.error('Failed to save mission results:', error);
  }
};

/**
 * 从 localStorage 加载任务结果
 */
export const loadMissionResults = (): MissionRecord[] | null => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.MISSION_RESULTS);
    return data ? JSON.parse(data) : null;
  } catch (error) {
    console.error('Failed to load mission results:', error);
    return null;
  }
};

/**
 * 保存玩家角色标记到 localStorage (V89: 支持多选)
 */
export const savePlayerRoleMarks = (marks: Record<number, RoleType[]>): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.PLAYER_ROLE_MARKS, JSON.stringify(marks));
  } catch (error) {
    console.error('Failed to save player role marks:', error);
  }
};

/**
 * 从 localStorage 加载玩家角色标记 (V89: 支持多选)
 */
export const loadPlayerRoleMarks = (): Record<number, RoleType[]> => {
  try {
    const data = localStorage.getItem(STORAGE_KEYS.PLAYER_ROLE_MARKS);
    // V89: 迁移旧数据格式（单选 -> 多选）
    if (data) {
      const parsed = JSON.parse(data);
      // 检查是否是旧格式（值为字符串而非数组）
      const isOldFormat = Object.values(parsed).some(v => typeof v === 'string');
      if (isOldFormat) {
        const newFormat: Record<number, RoleType[]> = {};
        Object.entries(parsed).forEach(([key, value]) => {
          if (typeof value === 'string') {
            newFormat[Number(key)] = [value as RoleType];
          }
        });
        return newFormat;
      }
      return parsed;
    }
    return {};
  } catch (error) {
    console.error('Failed to load player role marks:', error);
    return {};
  }
};

/**
 * 保存游戏笔记到 localStorage
 */
export const saveGameNotes = (notes: string): void => {
  try {
    localStorage.setItem(STORAGE_KEYS.GAME_NOTES, notes);
  } catch (error) {
    console.error('Failed to save game notes:', error);
  }
};

/**
 * 从 localStorage 加载游戏笔记
 */
export const loadGameNotes = (): string => {
  try {
    return localStorage.getItem(STORAGE_KEYS.GAME_NOTES) || '';
  } catch (error) {
    console.error('Failed to load game notes:', error);
    return '';
  }
};

/**
 * 清除所有游戏状态数据
 */
export const clearGameState = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEYS.CURRENT_GAME);
    localStorage.removeItem(STORAGE_KEYS.ROUND_DATA);
    localStorage.removeItem(STORAGE_KEYS.MISSION_RESULTS);
    localStorage.removeItem(STORAGE_KEYS.PLAYER_ROLE_MARKS);
    localStorage.removeItem(STORAGE_KEYS.GAME_NOTES);
    localStorage.removeItem(STORAGE_KEYS.IS_GAME_LOCKED);
  } catch (error) {
    console.error('Failed to clear game state:', error);
  }
};

/**
 * 检查是否存在未完成的游戏
 */
export const hasOngoingGame = (): boolean => {
  const state = loadGameState();
  return state !== null && state.currentRound <= 5;
};

/**
 * 获取游戏持续时间（毫秒）
 */
export const getGameDuration = (): number => {
  const state = loadGameState();
  if (!state) return 0;
  return Date.now() - state.lastUpdated;
};
